import { useEffect, useMemo, useState } from 'react'

import { heroSteps } from '@/content/site'
import { useInView } from '@/lib/useInView'
import { useReducedMotion } from '@/lib/useReducedMotion'

import { IconsPanel, LogoPanel, MoodboardPanel, PalettePanel, TypePanel } from './KitPanels'

const PANELS = [
  { key: 'palette', Span: 'sm:col-span-3', Comp: PalettePanel },
  { key: 'type', Span: 'sm:col-span-3', Comp: TypePanel },
  { key: 'icons', Span: 'sm:col-span-2', Comp: IconsPanel },
  { key: 'logo', Span: 'sm:col-span-2', Comp: LogoPanel },
  { key: 'moodboard', Span: 'sm:col-span-2', Comp: MoodboardPanel },
] as const

const HOLD_MS = 2600

/**
 * The hero: a brandkit assembling itself, one panel at a time, with the status
 * line narrating which pipeline stage produced it.
 *
 * Same shape as any reveal sequence — a timeout chain over a cumulative step
 * index — but the subject is the product's actual output rather than a
 * diagram of its architecture.
 */
export function BrandkitCanvas() {
  const reduced = useReducedMotion()
  const { ref: rootRef, inView } = useInView<HTMLDivElement>(0.05)
  const [stepIdx, setStepIdx] = useState(-1)

  const total = heroSteps.length

  useEffect(() => {
    // Reduced motion: everything already assembled, status settled, no timers.
    if (reduced) {
      setStepIdx(total - 1)
      return
    }

    if (!inView) return

    let cancelled = false
    const timers: number[] = []

    const runOnce = () => {
      if (cancelled) return
      setStepIdx(-1)

      let elapsed = 200
      heroSteps.forEach((_, idx) => {
        timers.push(
          window.setTimeout(() => {
            if (!cancelled) setStepIdx(idx)
          }, elapsed),
        )
        elapsed += 650
      })

      timers.push(
        window.setTimeout(() => {
          if (!cancelled) runOnce()
        }, elapsed + HOLD_MS),
      )
    }

    timers.push(window.setTimeout(runOnce, 300))

    return () => {
      cancelled = true
      timers.forEach((t) => window.clearTimeout(t))
    }
  }, [reduced, inView, total])

  // Cumulative: a panel, once shown, stays shown for the rest of the cycle.
  const shown = useMemo(() => {
    const set = new Set<string>()
    for (let i = 0; i <= stepIdx && i < total; i++) set.add(heroSteps[i].panel)
    return set
  }, [stepIdx, total])

  const status = stepIdx >= 0 ? heroSteps[stepIdx].status : 'Starting'
  const complete = stepIdx === total - 1

  return (
    <div
      ref={rootRef}
      className="glass glass-rim glass-hi relative overflow-hidden p-4 sm:p-5"
    >
      {/* Status bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span
          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full transition-colors duration-500 ${
            complete ? 'bg-aqua' : 'bg-iris'
          }`}
          aria-hidden="true"
        />
        <span
          className="font-mono text-[0.62rem] uppercase tracking-[0.16em] text-ink-dim"
          aria-live="polite"
        >
          {status}
        </span>

        <span className="ml-auto flex items-center gap-1" aria-hidden="true">
          {heroSteps.map((s, i) => (
            <span
              key={s.panel}
              className={`h-0.5 rounded-full transition-all duration-500 ${
                i <= stepIdx ? 'w-5 bg-ink/60' : 'w-2.5 bg-ink/15'
              }`}
            />
          ))}
        </span>
      </div>

      {/* Panels */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        {PANELS.map(({ key, Span, Comp }) => {
          const visible = shown.has(key)
          return (
            <div
              key={key}
              className={`${Span} glass rounded-2xl p-4 transition-all duration-700 ease-glass ${
                visible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
              }`}
              style={{ minHeight: '8.5rem' }}
            >
              <Comp />
            </div>
          )
        })}
      </div>
    </div>
  )
}
